import { AppInsightsService, genTinyGUID, SawDust } from '@cudirect/shared-ui-library';
import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from '@workflow/ecp-shared/service/api/api.service';
import { DataLoadURL } from '@workflow/ecp-shared/model/dataload-url';
import {
  CUDLConnect,
  PhoneNumber,
  PrefillAddress,
  PrefillApplicant,
  PrefillEmployer
} from '../../dynamic-app/component/building-block/app-prefill/app-prefill.type';
import { Applicant } from '../types/joint-applicant/applicant.class';
import { ApplicationApiService } from './application-api/application-api.service';
import { L360Service } from './l360/l360.service';
import { ApiResult } from './application-api/application-api.types';
import { PickList } from '../types/picklist';
import { StatefulService } from '@app/service/stateful.service';
import { getStateInfo, RegionStates } from 'src/postapp/consts/region-states.const';

@Injectable({
  providedIn: 'root'
})
export class HostPrefillService {
  private logger = inject(SawDust).withContext({ component: 'HostPrefillService' });
  /** Applicants information */
  public applicants: Applicant[];

  public status: 'notStarted' | 'inProgress' | 'completed' | 'error' = 'notStarted';
  public isError = false;
  public flowId: string;
  private isMockEnable = true;
  private orderComplete = {
    orderId: '',
    retryRequest: true,
    maxRetryTime: 60 // In seconds
  };
  private oaoEmpType = {
    unemployed: 'Unemployed',
    selfemployed: 'Self Employed',
    employed: 'Employed',
    retired: 'Retired'
  };
  private get isOlaFlow(): boolean {
    return this.flowId?.toLocaleLowerCase().includes('ola');
  }
  public hbPrefillComment = 'Applicant came from HomeBanking';

  private readonly statefulService = inject(StatefulService);

  constructor(
    private readonly api: ApiService,
    private readonly appApiService: ApplicationApiService,
    public readonly l360Service: L360Service,
    private readonly appInsightsService: AppInsightsService
  ) {}

  public get mockData(): HbUserInfo {
    return {
      firstName: 'William',
      lastName: 'Buzzerd',
      ssn: '666540270',
      middleName: '',
      accounts: []
    };
  }

  //   public sendingUserInformation(data: HbUserInfo, navigateNext: () => void): void {
  //     const prefillRequest = this.isMockEnable ? this.mockData : data;

  //     /** Making host prefill call with the response */
  //     this.consumerUserService.PostPrefillData(
  //       prefillRequest,
  //       (res) => {
  //         if (res && res.isSuccess) {
  //           this.orderComplete.orderId = res.data;
  //           this.getCompleteResponse(navigateNext);
  //           this.status = 'inProgress';
  //         } else {
  //           this.status = 'error';
  //           navigateNext();
  //         }
  //       },
  //       () => {
  //         // Error Handling After 60 seconds.
  //         this.handleError();
  //         navigateNext();
  //       }
  //     );
  //   }

  private handleError(): void {
    this.isError = true;
    this.status = 'error';
  }

  //   private getCompleteResponse(navigateNext: () => void) {
  //     this.consumerUserService.GetPrefillOrderComplete(
  //       this.orderComplete,
  //       (res) => {
  //         if (res && res.isSuccess) {
  //           this.status = 'completed';
  //           if (this.flowId.toLowerCase() === LoanType.OAO.toLowerCase()) {
  //             this.updatePrefillDataForOao(res.data, navigateNext);
  //           } else {
  //             this.updatePrefillDataToBundle(res.data, navigateNext);
  //           }
  //           return;
  //         } else {
  //           navigateNext();
  //         }
  //       },
  //       () => {
  //         this.handleError();
  //         navigateNext();
  //       }
  //     );
  //   }

  public getIdentificationtype = (idType: string): string => {
    const pickList = this.statefulService.wrap('ss:ola-identificationType-pick-list').valueString;
    const identification_picklist = pickList ? JSON.parse(pickList) : undefined;
    if (!identification_picklist || identification_picklist.length === 0) {
      return;
    }
    const idTypes = identification_picklist.filter((t) => t.value.toLowerCase() === 'identificationtype')[0].pickListItems;

    if (!idType || !idTypes || idTypes.length === 0) {
      return;
    }

    const id = idTypes.filter((t) => t.id === Number(idType))[0].value;
    return id;
  };

  public updatePrefillDataToBundle = (parsedData: CUDLConnect, navigateNext?: () => void): void => {
    if (!parsedData) {
      return;
    }
    const protocol = this.isOlaFlow ? 'ola' : 'api';
    const root = `${protocol}:borrowerInfo`;
    const applicants = parsedData?.cudlAccountLink?.applicant;
    if (!applicants || applicants.length === 0) {
      return;
    }
    // TODO: Eventually we will need to be able to select applicants in the UI. Hardcoding to [0] for now.
    const applicant = applicants[0];
    this.statefulService.wrap(`${root}/dateOfBirth`).value = this.parseDate(applicant.dateOfBirth);
    this.statefulService.wrap(`${root}/ssn`).value = applicant.ssn;
    this.statefulService.wrap(`mem:has-data-ola-consumer-personal-info`).value = true;
    this.statefulService.wrap(`mem:has-data-ola-secure-loan-personal-info`).value = true;
    this.statefulService.wrap(`${root}/maritalStatus`).value = applicant.maritalStatus;

    if (!this.isOlaFlow) {
      this.statefulService.wrap(`${root}/citizenshipStatus`).value = applicant.citizenshipStatus;
    }

    // TODO: Introduce logic to pull cell > home > work > other > fax in UI or API
    this.prefillPhoneInfo(root, applicant);
    this.prefillApplicantInfo(root, applicant);
    // TODO: Eventually we may want to be able to select addresses in the UI. Hardcoding for now.
    this.prefillAddressInfo(protocol, applicant);
    this.fillEmploymentDetail(protocol, applicant);
    this.prefillMembershiInfo(protocol, applicant);
    this.appApiService.saveBundleToDDMS(() => {
      navigateNext();
    });
  };

  /**
   * Prefill the applicant phone information from Homebanking
   * @param root storage root
   * @param applicant object contains applicant information
   */
  private prefillPhoneInfo = (root: string, applicant: PrefillApplicant): void => {
    if (applicant?.phoneNumber && applicant?.phoneNumber.length > 0) {
      const phone = this.filterPhone(applicant.phoneNumber);
      if (phone) {
        this.statefulService.wrap(`${root}/primaryPhoneNumber`).value = phone.number;
        this.statefulService.wrap(`${root}/phoneType`).value = phone.phoneNumberType;
      }
    }
  };

  /**
   * Prefill the applicant personal information from Homebanking
   * @param root storage root
   * @param applicant object contains applicant information
   */
  private prefillApplicantInfo = (root: string, applicant: PrefillApplicant): void => {
    if (!applicant?.name) {
      return;
    }
    this.statefulService.wrap(`${root}/firstName`).value = applicant.name.first;
    this.statefulService.wrap(`${root}/lastName`).value = applicant.name.last;
    this.statefulService.wrap(`${root}/emailAddress`).value = applicant.emailAddress.length ? applicant.emailAddress[0] : '';
    this.statefulService.wrap(`${root}/autoFillOption`).value = 'Prefill';
    this.statefulService.wrap(`${root}/autoFillStatus`).value = true;
    this.statefulService.wrap(`${root}/isPrefilled`).value = true;
    this.statefulService.wrap(`${root}/middleName`).value = applicant.name.middle;
    this.statefulService.wrap(`${root}/suffix`).value = applicant.name.suffix;
    this.statefulService.wrap(`${root}/citizenshipStatus`).value = applicant.citizenshipStatus;
  };

  /**
   * Prefill the applicant address information from Homebanking
   * @param protocol storage prefix ola/api
   * @param applicant object contains applicant information
   */
  private prefillAddressInfo = (protocol: string, applicant: PrefillApplicant): void => {
    if (!applicant?.address || applicant?.address?.length === 0) {
      return;
    }

    const addressroot = `${protocol}:borrowerInfo/addresses`;
    const length = this.statefulService.arraySize(addressroot);
    if (length > 0) {
      this.prefillResidentialHistory(protocol, addressroot, applicant);
    } else {
      this.statefulService.arrayAdd(addressroot, () => {
        this.prefillResidentialHistory(protocol, addressroot, applicant);
      });
    }
  };

  public normalizeState(address: PrefillAddress): void {
    const oldState = address.state;
    address.state = getStateInfo(address.state) || address.state;
    if (address.state !== oldState) {
      this.logger.debug('DataCleansing: State value updated', { from: oldState, to: address.state });
    }
  }

  /** Prefill Residential History */
  private prefillResidentialHistory(protocol: string, addressroot: string, applicant: PrefillApplicant): void {
    const address: PrefillAddress = applicant?.address[0];
    const length = this.statefulService.arraySize(addressroot);

    setTimeout(() => {
      const index = length - 1;
      if (address?.state && address?.state?.length !== 2) {
        this.normalizeState(address);
      }

      /** Setting Default value to country otherwise it will override the prefilled values */
      if (protocol !== 'ola') this.statefulService.wrap(`${addressroot}/${index}/country`).value = 'USA';
      this.statefulService.wrap(`${addressroot}/${index}/address1`).value = address.address1;
      this.statefulService.wrap(`${addressroot}/${index}/address2`).value = address.address2;
      this.statefulService.wrap(`${addressroot}/${index}/city`).value = address.city;
      this.statefulService.wrap(`${addressroot}/${index}/state`).value = address.state;
      this.statefulService.wrap(`${addressroot}/${index}/zip`).value = address.zip;
      if (address.timeAtAddress && this.isOlaFlow) {
        const { years, months } = this.convertMonthsToYear(address.timeAtAddress);
        this.statefulService.wrap(`${addressroot}/${index}/timeinProfessionMonths`).value = months;
        this.statefulService.wrap(`${addressroot}/${index}/timeinProfessionYears`).value = years;
      }
    }, 100);
  }

  /**
   * Get Home banking data by making API call
   * @param otuToken string
   * @param cb callback
   */
  public async getHbData(otuToken: string): Promise<CUDLConnect | void> {
    /** Homebanking Prefill tracking id */
    const hbPrefillEventPairId = genTinyGUID(24);
    this.logAppInsightsHBPrefillEvent(hbPrefillEventPairId, 'start', this.userEmail);
    // { data.internalJwtToken.access_token }

    return this.fetchHbData(otuToken, true)
      .then(
        (res: ApiResult<CUDLConnect>) => {
          if (res.isSuccess) {
            this.statefulService.wrap('mem:home-banking-prefill-data').value = JSON.stringify(res.data);
            this.logAppInsightsHBPrefillEvent(hbPrefillEventPairId, 'end', this.userEmail, 'success');
            // Do not hydrate oao immediately, but do it for loans
            const state = res.data?.cudlAccountLink?.applicant[0]?.address[0]?.state;
            if (state && state.length !== 2) {
              const stateAbbreviation = RegionStates[state];
              if (stateAbbreviation) {
                // this.logger.debug('fixing state');
                res.data.cudlAccountLink.applicant[0].address[0].state = stateAbbreviation.text;
              } else {
                // this.logger.debug('state ok');
              }
              // this.logger.debug('state', res.data.cudlAccountLink.applicant[0].address[0].state);
            }
            if (this.flowId.toLowerCase() !== 'oao') {
              this.updatePrefillDataToBundle(res.data, () => {
                return res.data;
              });
            }
            // OAO flow will hydrate on the next page
            return res.data;
          } else {
            // If we failed without an error here, it's an error that is not an HttpErrorResponse / edge case - lets try and capture it
            const errorResponse = `Reason: ${res?.reason || 'Unknown'} - Message: ${res?.message || 'Unknown'}`;
            this.logger.error('Error fetching HB Data: ', errorResponse);
            this.logAppInsightsHBPrefillEvent(hbPrefillEventPairId, 'end', this.userEmail, 'error', errorResponse);
          }
        },
        (err: HttpErrorResponse) => {
          this.logger.withError(err).error('Error fetching HB Data');
          this.logAppInsightsHBPrefillEvent(hbPrefillEventPairId, 'end', this.userEmail, 'error', JSON.stringify(err.error));
        }
      )
      .then((retval) => {
        // Finally, reset the hostPrefillToken state to an empty string
        this.statefulService.wrap(`${this.isOlaFlow ? 'ola' : 'api'}:hostPrefillToken`).value = '';
        this.statefulService.wrap(`${this.isOlaFlow ? 'ola' : 'api'}:isHostPrefillCompleted`).value = true;
        this.statefulService.wrap('ss:isHostPrefillCompleted').value = true;

        return retval;
      });
  }

  /**
   * Prefill Buisness Info page details
   * @param otuToken string
   */
  public async prefillBusinessInfo(otuToken: string): Promise<CUDLConnect | void> {
    return this.fetchHbData(otuToken, false).then(
      (res: ApiResult<CUDLConnect>) => {
        if (res.isSuccess) {
          const pageIterationId = 0;
          const path = 'businessInformation/responsibleParties/#';
          return this.checkAndAddApplicant(`api:oaoMemberInformation/${path}`).then(() => {
            if (res?.data?.cudlAccountLink?.applicant?.length) {
              const applicant = res.data.cudlAccountLink.applicant[0];
              if (!applicant) {
                return res?.data;
              }
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/firstName`, undefined, pageIterationId).value =
                applicant.name?.first;
              this.statefulService.wrap(`api:oaoMemberInformation/${path}/lastName`, undefined, pageIterationId).value =
                applicant.name?.last;
              this.statefulService.wrap('mem:isloading').value = false;
              return res?.data;
            }

            return res?.data;
          });
        }
      },
      (err: HttpErrorResponse) => {
        this.logger.withError(err).warn('Error fetching HB Data');
      }
    );
  }

  /**
   * Update Prefill Data for OAO
   */
  public updatePrefillDataForOao = (
    parsedData: CUDLConnect,
    navigateNext?: () => void,
    path: string = 'primaryApplicant',
    pageIterationId: number = undefined
  ): void => {
    if (parsedData) {
      if (parsedData.cudlAccountLink && parsedData.cudlAccountLink.applicant.length) {
        const applicant = parsedData.cudlAccountLink.applicant[0];

        // Prefill OAO Personal Information
        this.prefillOAOPersonalInformation(applicant, path, pageIterationId);

        // Fill OAO Address Info
        this.prefillOAOAddressInformation(applicant, path, pageIterationId);

        // Fill OAO Employment Information
        this.prefillOAOEmploymentInformation(applicant, path, pageIterationId);

        // Fill OAO Identification Info
        this.prefillOAOIdentificationInformation(applicant, path, pageIterationId);

        // Fill OAO Membership Info
        this.prefillOAOMembershipInformation(applicant);
        this.appApiService.saveBundleToDDMS(navigateNext);
      }
    }
  };

  /**
   * Prefill OAO Personal Information from Homebanking
   * @param applicant object contains applicant information
   * @param path storage path for OAO
   * @param pageIterationId iteration value
   */
  private prefillOAOPersonalInformation(applicant: PrefillApplicant, path: string, pageIterationId: number) {
    if (!applicant) {
      return;
    }

    const home = applicant.phoneNumber.filter((p) => p.phoneNumberType === 'HomePhone');
    const mobile = applicant.phoneNumber.filter((p) => p.phoneNumberType === 'Mobile');
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/firstName`, undefined, pageIterationId).value = applicant.name?.first;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/middleName`, undefined, pageIterationId).value = applicant.name?.middle;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/lastName`, undefined, pageIterationId).value = applicant.name?.last;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillOption`, undefined, pageIterationId).value = 'Prefill';
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/isPrefilled`, undefined, pageIterationId).value = true;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/autoFillStatus`, undefined, pageIterationId).value = true;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/homePhone`, undefined, pageIterationId).value = home.length
      ? home[0].number
      : '';
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/ssn`, undefined, pageIterationId).value = applicant.ssn;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/email`, undefined, pageIterationId).value = applicant.emailAddress.length
      ? applicant.emailAddress[0]
      : '';
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/mobileNumber`, undefined, pageIterationId).value = mobile.length
      ? mobile[0].number
      : '';

    if (!mobile.length && !!home.length) {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/phoneType`, undefined, pageIterationId).value = 'HomePhone';
    } else {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/phoneType`, undefined, pageIterationId).value = 'Mobile';
    }

    this.statefulService.wrap(`api:oaoMemberInformation/${path}/citizenshipStatus`, undefined, pageIterationId).value =
      applicant.citizenshipStatus;

    if (applicant.dateOfBirth) {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/dateOfBirth`, undefined, pageIterationId).value = this.parseDate(
        applicant.dateOfBirth
      );
    }
    const mothersMaidenName = applicant.otherData && applicant.otherData.mothersMaidenName;
    if (mothersMaidenName) {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/mothersMaidenName`, undefined, pageIterationId).value = mothersMaidenName;
    }

    if (path.toLowerCase().includes('business')) {
      this.statefulService.wrap(`mem:has-data-oao-business-application-fields-#`, undefined, pageIterationId).value = true;
    } else {
      this.statefulService.wrap(`mem:has-data-oao-application-fields`).value = true;
      this.statefulService.wrap(`mem:has-data-oao-application-fields-youth`).value = true;
    }
  }

  /**
   * Prefill OAO Address Information from Homebanking
   * @param applicant object contains applicant information
   * @param path storage path for OAO
   * @param pageIterationId iteration value
   */
  private prefillOAOAddressInformation(applicant: PrefillApplicant, path: string, pageIterationId: number) {
    if (!applicant) {
      return;
    }

    const addresses = applicant.address && applicant.address.filter((a) => a.addressType && a.addressType.toLowerCase() === 'home');
    if (addresses && addresses.length > 0) {
      let addRoot = `api:oaoMemberInformation/${path}/primaryAddress`;
      const timeout = 0;
      addRoot = addRoot.includes('#') ? addRoot.replace('#', pageIterationId.toString()) : addRoot;
      const preloadedSize = this.statefulService.arraySize(addRoot);
      const setAddress = () => {
        addresses.forEach((element, index) => {
          // Rewrite exxistinngData on Prefill
          if (index < preloadedSize) {
            this.setPrimaryAddress(path, pageIterationId, index, element);
          } else {
            // Add new Item if the prefill address exceeds the items
            this.statefulService.arrayAdd(addRoot, () => {
              setTimeout(() => {
                this.setPrimaryAddress(path, pageIterationId, index, element);
              }, timeout);
            });
          }
        });
      };
      // Rare scenario if the existing application has more address than prefill response truncate and add new.
      if (preloadedSize > addresses.length) {
        this.statefulService.arrayTruncate(addRoot, () => {
          setTimeout(() => {
            setAddress();
          }, timeout);
        });
      } else {
        setAddress();
      }
    }
  }

  /**
   * Set Primary Address from prefill response
   * @param path path of an applicant
   * @param pageIterationId current page iterationId
   * @param index index of an address to be added
   * @param element prefill address
   */
  private setPrimaryAddress(path: string, pageIterationId: number, index, element: PrefillAddress) {
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/addressLine1`, index, pageIterationId).value =
      element.address1;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/addressLine2`, index, pageIterationId).value =
      element.address2;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/city`, index, pageIterationId).value = element.city;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/state`, index, pageIterationId).value = element.state;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/zipCode`, index, pageIterationId).value = element.zip;
    if (element.timeAtAddress) {
      const { years, months } = this.convertMonthsToYear(element.timeAtAddress);
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/years`, index, pageIterationId).value = years;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/primaryAddress/$/months`, index, pageIterationId).value = months;
    }
  }

  /**
   * Prefill OAO Employment Information from Homebanking
   * @param applicant object contains applicant information
   * @param path storage path for OAO
   * @param pageIterationId iteration value
   */
  private prefillOAOEmploymentInformation(applicant: PrefillApplicant, path: string, pageIterationId: number) {
    if (!applicant) {
      return;
    }

    const employers = applicant.employer;
    if (employers && employers.length > 0) {
      let empRoot = `api:oaoMemberInformation/${path}/employments`;
      empRoot = empRoot.includes('#') ? empRoot.replace('#', pageIterationId.toString()) : empRoot;
      const employesSize = this.statefulService.arraySize(empRoot) || 0;
      if (employesSize === 0) {
        this.statefulService.arrayAdd(empRoot, () =>
          setTimeout(() => {
            this.addEmployment(path, pageIterationId, employers);
          }, 0)
        );
      } else if (employesSize > 1) {
        this.statefulService.arrayTruncate(empRoot, () => {
          setTimeout(() => {
            this.statefulService.arrayAdd(empRoot, () => this.addEmployment(path, pageIterationId, employers));
          }, 0);
        });
      } else {
        this.addEmployment(path, pageIterationId, employers);
      }
    }
  }

  // Add Employment from prefill data
  private addEmployment(path: string, pageIterationId: number, employers: PrefillEmployer[]) {
    const employerElement = employers[0];
    const empType = employerElement.employmentType && employerElement.employmentType.toLowerCase();

    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/employerName`, 0, pageIterationId).value =
      employerElement.name;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/jobTitle`, 0, pageIterationId).value =
      employerElement.occupation;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/employmentStatus`, 0, pageIterationId).value =
      this.oaoEmpType[empType];
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/startDate`, 0, pageIterationId).value =
      employerElement.startDate;
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/income`, 0, pageIterationId).value =
      employerElement.annualIncome;
    // Income always converted to Annual in API
    this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/frequency`, 0, pageIterationId).value = 'Yearly';

    if (employerElement.periodOfEmployment) {
      const { years, months } = this.convertMonthsToYear(employerElement.periodOfEmployment);
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/months`, 0, pageIterationId).value = months;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/employments/$/years`, 0, pageIterationId).value = years;
    }
  }

  /**
   * Prefill OAO Identification Information from Homebanking
   * @param applicant object contains applicant information
   * @param path storage path for OAO
   * @param pageIterationId iteration value
   */
  private prefillOAOIdentificationInformation(applicant: PrefillApplicant, path: string, pageIterationId: number) {
    if (!applicant) {
      return;
    }

    const identification = applicant.otherData && applicant.otherData.applicantIdentification;
    if (identification && identification.length > 0) {
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idType`, undefined, pageIterationId).value =
        identification[0].identificationTypeID;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idExpirationDate`, undefined, pageIterationId).value = this.parseDate(
        identification[0].expirationDate
      );
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idNumber`, undefined, pageIterationId).value = identification[0].number;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idIssuedDate`, undefined, pageIterationId).value = this.parseDate(
        identification[0].issuedDate
      );
      const issuedBy = identification[0].issuedByState ? identification[0].issuedByState : identification[0].issuedByStateAbbreviation;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idIssuedBy`, undefined, pageIterationId).value = issuedBy;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idIssuedByState`, undefined, pageIterationId).value = issuedBy;
      this.statefulService.wrap(`api:oaoMemberInformation/${path}/idIssuedByCountry`, undefined, pageIterationId).value =
        identification[0].issuedByCountryName;
    }
  }

  /**
   * Prefill OAO Membership Information from Homebanking
   * @param applicant object contains applicant information
   * @param path storage path for OAO
   * @param pageIterationId iteration value
   */
  private prefillOAOMembershipInformation(applicant: PrefillApplicant) {
    if (!applicant) {
      return;
    }

    this.statefulService.wrap('api:oaoMemberInformation/alreadyMember').value = true;

    const memberAccountNumber = applicant.memberAccountNumber;
    if (memberAccountNumber && memberAccountNumber.toString().length > 0) {
      this.statefulService.wrap('api:oaoMemberInformation/memberId').value = memberAccountNumber;
      this.statefulService.wrap('api:oaoMemberInformation/hbPrefillComment').value = this.hbPrefillComment;
    }
  }

  /**
   * Prefill the applicant employment information from Homebanking
   * @param protocol storage prefix ola/api
   * @param applicant object contains applicant information
   */
  private fillEmploymentDetail(protocol: string, applicant: PrefillApplicant) {
    if (!applicant.employer || applicant.employer.length === 0) {
      return;
    }
    const picklistTypes = this.l360Service.getPickListTypes('EmployerType');
    if (picklistTypes?.length > 0) {
      // Add Current Employment Details
      const currentemproot = `${protocol}:employments`;
      const length = this.statefulService.arraySize(currentemproot);
      applicant.employer.forEach((employee, i) => {
        if (i === 0) {
          const selectedPickListItem = picklistTypes[0].pickListItems.find(
            (pickListItem) => pickListItem?.value.trim().toLowerCase() === employee.employmentType?.trim().toLowerCase()
          );
          employee.isCurrentEmployer = true;
          if (length <= i + 1) {
            // Updating existing item in array
            this.createEmployement(currentemproot, employee, selectedPickListItem ? selectedPickListItem.id : null, i);
          } else {
            // creating new item if the index is greater than array length
            this.statefulService.arrayAdd(currentemproot, () => {
              this.createEmployement(currentemproot, employee, selectedPickListItem ? selectedPickListItem.id : null, i);
            });
          }
        }
      });

      this.addAdditionalEmployment(protocol, applicant, picklistTypes);
    }
  }

  /**
   * Method to add Additional Employment data
   * @param protocol - string to determine ola or api
   * @param applicant - current applicant
   * @param picklistTypes - array of picklist items
   */
  private addAdditionalEmployment(protocol: string, applicant: PrefillApplicant, picklistTypes: PickList[]): void {
    if (!applicant.employer || applicant.employer.length <= 1) {
      return;
    }
    // Add Additional Employment Details
    const additionalemproot = `${protocol}:additionalEmployments`;
    applicant.employer.forEach((employee, i) => {
      if (i > 0) {
        const selectedPickListItem = picklistTypes[0].pickListItems.find(
          (pickListItem) => pickListItem?.value.trim().toLowerCase() === employee.employmentType?.trim().toLowerCase()
        );
        employee.isCurrentEmployer = false;
        // With ola:additionalEmployments always [] to begin with, creating new item
        this.statefulService.arrayAdd(additionalemproot, () => {
          this.createEmployement(additionalemproot, employee, selectedPickListItem ? selectedPickListItem.id : null, i - 1);
        });
      }
    });
  }

  /**
   * Create employment object & updte into bundle
   * @param emproot storageid path
   * @param employee employee object
   * @param employmentIdType employmet type
   * @param index int
   */
  private createEmployement = (emproot: string, employee: PrefillEmployer, employmentIdType, index: number) => {
    setTimeout(() => {
      this.statefulService.wrap(`${emproot}/${index}/current`).value = employee.isCurrentEmployer;
      this.statefulService.wrap(`${emproot}/${index}/employmentType`).value = employmentIdType ? employmentIdType.toString() : '';
      this.statefulService.wrap(`${emproot}/${index}/employerPhonenumber`).value = employee.phoneNumber && employee.phoneNumber.number;
      this.statefulService.wrap(`${emproot}/${index}/employerorBusinessName`).value = employee.name;
      this.statefulService.wrap(`${emproot}/${index}/positionOrTitle`).value = employee.occupation;

      if (employee.periodOfEmployment && this.isOlaFlow) {
        const { years, months } = this.convertMonthsToYear(employee.periodOfEmployment);
        this.statefulService.wrap(`${emproot}/${index}/timeinProfessionMonths`).value = months;
        this.statefulService.wrap(`${emproot}/${index}/timeinProfessionYears`).value = years;
      }
      this.setFrequencyIncome(employee, emproot, index);
    }, 100);
  };

  private setFrequencyIncome(employee: PrefillEmployer, emproot: string, index: number) {
    const frequency = employee.incomeFrequency;
    let payRate = '';
    let payFrequency = '';
    switch (frequency) {
      case 'Yearly':
        payFrequency = 'Annually';
        break;
      case 'Monthly':
        payFrequency = 'Monthly';
        payRate = employee.income;
        break;
      case 'BiWeekly':
        payFrequency = 'Biweekly';
        break;
      case 'Weekly':
        payFrequency = 'Weekly';
        break;
    }

    if (payFrequency) {
      this.statefulService.wrap(`${emproot}/${index}/payFrequency`).value = payFrequency;
    }
    if (payRate) {
      this.statefulService.wrap(`${emproot}/${index}/monthlyIncome`).value = payRate;
    }
    if (this.isOlaFlow) {
      // Income always converted to Annual in API
      this.statefulService.wrap(`${emproot}/${index}/grossIncome`).value = employee.annualIncome;
      this.statefulService.wrap(`${emproot}/${index}/payFrequency`).value = 'Annually';
    }
  }

  /**
   * Prefill the applicant identification information from Homebanking
   */
  public fillIdentificationDetail() {
    const data = this.statefulService.wrap('mem:home-banking-prefill-data').valueString;
    if (!data) {
      return;
    }

    this.statefulService.wrap('mem:home-banking-prefill-data').value = null;
    const parsedData = JSON.parse(data) as CUDLConnect;
    if (!parsedData) {
      return;
    }

    const applicants = parsedData?.cudlAccountLink?.applicant;
    if (!applicants || applicants.length === 0) {
      return;
    }
    const applicant = applicants[0];
    const root = 'ola:borrowerInfo';

    const identifications = applicant?.otherData?.applicantIdentification;
    if (!this.isOlaFlow || !identifications || identifications?.length === 0) {
      return;
    }

    const identification = identifications[0];
    const idType = identification.identificationTypeID;

    const picklistTypes = this.l360Service.getPickListTypes('IdentificationType_Fields, IdentificationType');
    if (picklistTypes?.length > 0) {
      const idTypes = picklistTypes.filter((t) => t.value.toLowerCase() === 'identificationtype')[0].pickListItems;
      const list = idTypes.filter((t) => t.id === Number(idType));
      if (list?.length > 0) {
        const id = list[0].value;
        this.statefulService.wrap(`${root}/idType`, 0).value = id ? id : '';
        this.statefulService.wrap(`${root}/idNumber`, 0).value = identification.number;
        this.statefulService.wrap(`${root}/issuingCountry`, 0).value = identification.issuedByCountryName;
        this.statefulService.wrap(`${root}/issuingState`, 0).value = identification.issuedByState
          ? identification.issuedByState
          : identification.issuedByStateAbbreviation;
        this.statefulService.wrap(`${root}/expirationDate`, 0).value = this.parseDate(identification.expirationDate);
        this.statefulService.wrap(`${root}/issuedDate`, 0).value = this.parseDate(identification.issuedDate);
      }
    }
  }

  /**
   * Prefill the membershi information from Homebanking
   * @param protocol storage prefix ola/api
   * @param applicant object contains applicant information
   */
  private prefillMembershiInfo = (protocol: string, applicant: PrefillApplicant): void => {
    if (this.isOlaFlow) {
      this.statefulService.wrap(`${protocol}:olaApplicationInformation/membership/isMember`).value = true;

      const memberAccountNumber = applicant.memberAccountNumber;
      if (memberAccountNumber && memberAccountNumber.toString().length > 0) {
        this.statefulService.wrap(`${protocol}:olaApplicationInformation/membership/membershipID`).value = memberAccountNumber;
        this.statefulService.wrap(`${protocol}:olaApplicationInformation/hbPrefillComment`).value = this.hbPrefillComment;
      }
    }

    if (!applicant?.otherData?.financialInstitutionData) {
      return;
    }

    if (!this.isOlaFlow) {
      this.statefulService.wrap(`${protocol}:membershipInfo/membershipNumber`).value =
        applicant.otherData.financialInstitutionData.membershipNumber;
      const membershipType = Number(applicant.otherData.financialInstitutionData.typeOfMember);
      this.statefulService.wrap(`${protocol}:membershipInfo/membershipType`).value = isNaN(membershipType) ? '' : membershipType;
    }
  };

  /**
   * Retrieves HB data for the given OTU token and processes the result to update
   * prefill data accordingly.
   *
   * @param otuToken - The token associated with OTU (One Time Use).
   * @param cb - A callback function to be executed after data retrieval and processing.
   *
   * @remarks
   * This method uses the `consumerUserService.GetHbData` to fetch HB data. Depending on
   * the success of this operation and the current flow type (`flowId`), it will either
   * update the prefill data for an OAO or OLA
   * The result is also influenced by the type of membership (e.g., business).
   * Additionally, it will reset the hostPrefillToken state to an empty string.
   *
   */

  // eslint-disable-next-line @typescript-eslint/require-await
  public async fetchHbData(otuToken: string, clearCache: boolean): Promise<ApiResult<CUDLConnect>> {
    return this.api.requestApi<ApiResult<CUDLConnect>>({
      urlObj: {
        hostName: 'consumerPortalApi',
        method: 'GET',
        url: 'redirect/getPrefillOtuTokenData'
      } as DataLoadURL,
      queryParam: { otuToken, clearCache }
    });
  }

  /** Getter for OLA or OAO agnostic user email */
  public get userEmail(): string {
    return (
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/email').valueString ??
      this.statefulService.wrap('ola:borrowerInfo/emailAddress').valueStringOnly ??
      'Unknown'
    );
  }

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.appInsightsService.logEvent(eventName, eventData);
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Logs an event in Application Insights, typically called when an account submission process either starts or ends.
   * This method is specifically meant for tracking the status and associated details of each loan submission attempt.
   *
   * @param correlationId - Unique identifier to correlate events in a single transaction.
   * @param eventState - Represents the state of the event, whether it's 'start' or 'end'.
   * @param appNumber - Unique identifier of the account which is being processed.
   * @param user - Identifier for the user triggering this event, could be username or user ID.
   * @param status - Optional parameter that captures the status of the event. It can either be 'success' or 'error'.
   * @param errorDetails - Optional parameter that captures the details of the error, if any.
   *
   * @returns This method doesn't have a return.
   */
  private logAppInsightsHBPrefillEvent(
    correlationId: string,
    eventState: 'start' | 'end',
    user: string,
    status: 'success' | 'error' = null,
    errorDetails: string = null
  ): void {
    const source = 'ecp.web';
    this.logEvent('ecp.ui.homebankingPrefill', {
      correlationId,
      eventState,
      user,
      status,
      source,
      errorDetails
    });
  }

  /**
   * Tech Story 287765: Revisit and fix Date of Birth formating from host-prefill
   * Replacing '/' with '-' to fix, date formating issue when converting string to date.
   */
  private parseDate = (date: string): string => {
    if (date) {
      const datepipe: DatePipe = new DatePipe('en-US');
      date = date.replace(/-/g, '/');
      const newDate = new Date(date);
      const parsedDate = datepipe.transform(newDate, 'MM/dd/yyyy');
      return parsedDate;
    }
    return '';
  };
  private filterPhone = (phoneNumber: PhoneNumber[]): PhoneNumber => {
    const reqPhoneType = ['Mobile', 'HomePhone', 'WorkPhone'];
    let phone;
    for (const element of reqPhoneType) {
      phone = phoneNumber.find((t) => t.phoneNumberType === element);
      if (phone) break;
    }
    return phone || (phoneNumber.length && phoneNumber[0]);
  };
  private convertMonthsToYear(totalMonths: number) {
    let years = 0;
    let months = 0;
    totalMonths = Number(totalMonths);
    if (!isNaN(totalMonths)) {
      years = Math.floor(totalMonths / 12);
      months = totalMonths % 12;
    }
    return { years, months };
  }

  public checkAndAddApplicant(root: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (root && root.includes('#')) {
        root = root.replace('/#', '');
        const size = this.statefulService.arraySize(root);
        if (size === undefined || size <= 0) {
          this.statefulService.arrayAdd(root, () => {
            this.statefulService.wrap('api:oaoMemberInformation/businessInformation/responsiblePartyCount').value = 1;
            resolve();
          });
          return;
        }
      }
      resolve();
    });
  }
}

export interface UserAuthorization {
  authorization: string;
  command?: string;
}

export interface HbUserInfo {
  accounts: string[];
  firstName: string;
  lastName: string;
  middleName?: string;
  ssn?: string;
  orderId?: string;
}
